/**
 * LA LEYENDA DEL EQUIPO EN LA AGENDA (administración, 14/10/2026)
 * ---------------------------------------------------------------
 * «Debajo del indicador de médico visitante, con el mismo formato, una
 * pequeña leyenda que diga si ese día algún miembro no médico está de
 * vacaciones, teletrabajando o de día libre»: «Equipo: Nombre — Vacaciones ·
 * Otra — Teletrabajo». Al mirar las agendas de cualquier día se ve de un
 * vistazo quién falta o quién está en otra modalidad.
 *
 * Es UNA por día, no por columna: el equipo no es de ninguna agenda. Por eso
 * va donde se ve una vez (CitasPage):
 *  - una agenda en vista de día: bajo la barra del día, donde está el médico
 *    visitante, y encima de la rejilla;
 *  - varias agendas a la vez: encima de las columnas (el visitante va en la
 *    cabecera de cada columna; esto, una sola vez para todas);
 *  - vista de semana: encima de la rejilla, una línea por día que tenga a
 *    alguien («Equipo · lun 12: …»).
 *
 * Solo lectura y solo nombre y tipo (lo que da el servidor:
 * GET /api/ausencias/leyenda), con «(Cuadrante)» si sale de los turnos
 * «Vacaciones» o «Libre» del Cuadrante. La ve todo el que ve la Agenda. Sin
 * nadie, no pinta nada; si falla, tampoco (la agenda se ve igual). En el
 * móvil la línea se parte en varias: nada de desplazarse de lado. También en
 * los días sin rejilla (la agenda no abre ese día, la semana sin horario,
 * «Todas» sin pacientes): encima del aviso, una vez (revisión del 14/10/2026).
 *
 * Mismo formato que `VisitantesColumna` (MedicoVisitanteDia.jsx): letra
 * pequeña, un icono y la etiqueta en negrita; cada persona con un cuadradito
 * del color de su tipo (los de Registro de jornada ▸ Vacaciones y
 * teletrabajo).
 */
import React, { useEffect, useState } from 'react';
import { Users } from 'lucide-react';
import { TEXTO_TIPO, claseTipo, diaCorto, leyendaEquipo, textoPersonas, textoTipo } from '../../lib/ausencias';

/** Una línea: «Equipo: Nombre — Vacaciones · Otra — Teletrabajo». */
export function LineaEquipo({ personas, etiqueta = 'Equipo', testId, className = '' }) {
  const lista = (personas || []).filter((p) => p?.nombre && TEXTO_TIPO[p.tipo]);
  if (!lista.length) return null;
  return (
    <p
      className={`flex min-w-0 items-start gap-1 px-1 text-[11px] leading-4 text-[#13231E]/80 ${className}`}
      title={`${etiqueta}: ${textoPersonas(lista)}`}
      data-testid={testId}
    >
      <Users className="mt-0.5 h-3 w-3 shrink-0 text-[#13231E]/60 dark:text-white/60" aria-hidden="true" />
      <span className="min-w-0 break-words">
        <span className="font-semibold text-[#13231E]">{etiqueta}:</span>{' '}
        {lista.map((p, i) => (
          <React.Fragment key={`${p.nombre}-${p.tipo}-${p.origen || ''}`}>
            {i > 0 && ' · '}
            <span data-tipo={p.tipo} data-origen={p.origen || undefined}>
              <span className={`mr-0.5 inline-block h-2 w-2 rounded-[2px] align-baseline ${claseTipo(p.tipo)}`} aria-hidden="true" />
              {p.nombre} — {textoTipo(p)}
            </span>
          </React.Fragment>
        ))}
      </span>
    </p>
  );
}

/**
 * Lo pide al servidor y lo pinta. `fechas`: el día que se mira (vista de día)
 * o los de la semana (vista de semana: una línea por día, con su «lun 12»).
 * `refreshKey`: se vuelve a pedir con cada recarga de la agenda.
 * `className`: para el hueco con lo de debajo (solo si hay algo que pintar).
 */
export default function LeyendaEquipo({
  fechas, refreshKey = 0, testId = 'citas-leyenda-equipo', className = '',
}) {
  const lista = (fechas || []).filter(Boolean);
  const desde = lista[0] || null;
  const hasta = lista[lista.length - 1] || null;
  const clave = `${desde}|${hasta}`;
  // Lo leído, con el rango al que pertenece: nunca se enseña el de otro día.
  const [leido, setLeido] = useState({ clave: null, dias: {} });

  useEffect(() => {
    if (!desde) return undefined;
    let cancelado = false;
    const k = `${desde}|${hasta}`;
    leyendaEquipo(desde, hasta)
      .then((dias) => { if (!cancelado) setLeido({ clave: k, dias }); })
      // Si falla una recarga del mismo día, se queda lo que ya se veía.
      .catch(() => {
        if (!cancelado) setLeido((prev) => (prev.clave === k ? prev : { clave: k, dias: {} }));
      });
    return () => { cancelado = true; };
  }, [desde, hasta, refreshKey]);

  if (!desde || leido.clave !== clave) return null;
  if (lista.length === 1) {
    return <LineaEquipo personas={leido.dias[desde]} testId={testId} className={className} />;
  }
  const conAlguien = lista.filter((f) => (leido.dias[f] || []).length > 0);
  if (!conAlguien.length) return null;
  return (
    <div className={`space-y-0.5 ${className}`} data-testid={testId}>
      {conAlguien.map((f) => (
        <LineaEquipo key={f} personas={leido.dias[f]} etiqueta={`Equipo · ${diaCorto(f)}`}
          testId={`${testId}-${f}`} />
      ))}
    </div>
  );
}
